import {
  Check,
  Pause,
  XCircle,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

// ────────────────────────────────────────────────────────────────────────────
// Types & Interfaces
// ────────────────────────────────────────────────────────────────────────────

export type NodeState = 'completed' | 'current' | 'future'

export interface Phase {
  number: number
  name: string
  icon: LucideIcon
  description: string
}

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────

const GOLD = '#b8a378'
const GOLD_DARK = '#9a8a6a'
const GRAY = '#d4d4d4'

// ────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ────────────────────────────────────────────────────────────────────────────

function getNodeState(phaseNumber: number, currentPhase: number): NodeState {
  if (currentPhase === 0) return 'future'
  if (phaseNumber < currentPhase) return 'completed'
  if (phaseNumber === currentPhase) return 'current'
  return 'future'
}

function stateLabel(state: NodeState, isPaused: boolean): string {
  if (state === 'completed') return 'Concluído'
  if (state === 'current') return isPaused ? 'Pausado' : 'Em andamento'
  return 'Pendente'
}

function stateBadgeClass(state: NodeState, isPaused: boolean): string {
  if (state === 'completed') return 'bg-green-100 text-green-700'
  if (state === 'current')
    return isPaused ? 'bg-yellow-100 text-yellow-700' : 'bg-amber-100 text-amber-700'
  return 'bg-neutral-100 text-neutral-400'
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop Flow Node
// ────────────────────────────────────────────────────────────────────────────

function FlowNode({
  phase,
  state,
  isSelected,
  onClick,
  isPaused,
  isCancelled,
}: {
  phase: Phase
  state: NodeState
  isSelected: boolean
  onClick: () => void
  isPaused: boolean
  isCancelled: boolean
}) {
  const Icon = phase.icon
  const isCompleted = state === 'completed' && !isCancelled
  const isCurrent = state === 'current' && !isCancelled

  let iconEl: React.ReactNode
  if (isCancelled) {
    iconEl = <XCircle className="h-4 w-4 text-white" />
  } else if (isCompleted) {
    iconEl = <Check className="h-4 w-4 text-white" />
  } else if (isCurrent && isPaused) {
    iconEl = <Pause className="h-4 w-4" style={{ color: GOLD }} />
  } else if (isCurrent) {
    iconEl = <Icon className="h-4 w-4" style={{ color: GOLD }} />
  } else {
    iconEl = <Icon className="h-4 w-4 text-neutral-300" />
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 focus:outline-none group"
    >
      <div className="relative">
        {/* Animated glow ring for current phase */}
        {isCurrent && (
          <div
            className="absolute -inset-1.5 rounded-full"
            style={{ animation: 'glow-pulse 2.5s ease-in-out infinite' }}
          />
        )}

        <div
          className={`
            relative w-10 h-10 lg:w-11 lg:h-11 rounded-full flex items-center justify-center
            transition-all duration-300
            ${isSelected ? 'scale-110' : 'group-hover:scale-105'}
          `}
          style={{
            ...(isCancelled
              ? { backgroundColor: '#e5e5e5' }
              : isCompleted
                ? { background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)` }
                : isCurrent
                  ? { border: `2.5px solid ${GOLD}`, backgroundColor: 'white' }
                  : { border: '2px dashed #d4d4d4', backgroundColor: '#fafafa' }),
            ...(isSelected
              ? { boxShadow: `0 0 0 3px rgba(184,163,120,0.2), 0 4px 14px rgba(184,163,120,0.15)` }
              : isCompleted
                ? { boxShadow: '0 2px 8px rgba(184,163,120,0.25)' }
                : {}),
          }}
        >
          {iconEl}
        </div>
      </div>

      <span
        className={`text-[11px] leading-tight text-center transition-all duration-200 ${
          isCancelled
            ? 'text-neutral-400'
            : isSelected
              ? 'text-neutral-900 font-semibold'
              : isCurrent
                ? 'text-neutral-800 font-medium'
                : isCompleted
                  ? 'text-neutral-600 font-medium'
                  : 'text-neutral-400'
        }`}
      >
        {phase.name}
      </span>
    </button>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop Flow Connector
// ────────────────────────────────────────────────────────────────────────────

function FlowConnector({ filled }: { filled: boolean }) {
  return (
    <div className="flex-1 flex items-center mx-0.5 mt-[18px] lg:mt-[20px]">
      {filled ? (
        <div className="relative w-full h-[2px] rounded-full overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(90deg, ${GOLD}, ${GOLD_DARK})` }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'flow-shimmer 3s ease-in-out infinite',
            }}
          />
        </div>
      ) : (
        <div
          className="h-[2px] w-full"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, #e0e0e0 0px, #e0e0e0 4px, transparent 4px, transparent 8px)`,
          }}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile Flow Node
// ────────────────────────────────────────────────────────────────────────────

function MobileFlowNode({
  phase,
  state,
  isSelected,
  onClick,
  isPaused,
  isCancelled,
  isLast,
}: {
  phase: Phase
  state: NodeState
  isSelected: boolean
  onClick: () => void
  isPaused: boolean
  isCancelled: boolean
  isLast: boolean
}) {
  const Icon = phase.icon

  let dotStyle: React.CSSProperties = {}
  let dotClass = ''
  let iconEl: React.ReactNode

  if (isCancelled) {
    dotStyle = { backgroundColor: '#d4d4d4', borderColor: '#a3a3a3' }
    iconEl = <XCircle className="h-4 w-4 text-white" />
  } else if (state === 'completed') {
    dotStyle = { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`, borderColor: GOLD }
    iconEl = <Check className="h-4 w-4 text-white" />
  } else if (state === 'current') {
    dotStyle = { borderColor: GOLD }
    dotClass = 'bg-white animate-pulse'
    iconEl = isPaused ? (
      <Pause className="h-4 w-4" style={{ color: GOLD }} />
    ) : (
      <Icon className="h-4 w-4" style={{ color: GOLD }} />
    )
  } else {
    dotClass = 'bg-white border-dashed border-neutral-300'
    iconEl = <Icon className="h-4 w-4 text-neutral-300" />
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-3 text-left w-full focus:outline-none p-2 -mx-2 rounded-lg transition-colors duration-200 ${
        isSelected ? 'bg-neutral-50' : ''
      }`}
    >
      {/* Rail */}
      <div className="flex flex-col items-center">
        <div
          className={`h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-xl border-2 ${dotClass}`}
          style={dotStyle}
        >
          {iconEl}
        </div>
        {!isLast && (
          <div
            className="w-[3px] flex-1 min-h-[20px] mt-1 rounded-full"
            style={
              state === 'completed' || state === 'current'
                ? { background: `linear-gradient(180deg, ${GOLD}, ${GOLD_DARK})` }
                : {
                    backgroundImage: `repeating-linear-gradient(180deg, ${GRAY} 0px, ${GRAY} 4px, transparent 4px, transparent 8px)`,
                  }
            }
          />
        )}
      </div>

      {/* Text */}
      <div className="pt-0.5 pb-2 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-sm font-semibold ${
              state === 'future' && !isCancelled ? 'text-neutral-400' : 'text-neutral-900'
            }`}
          >
            {phase.number}. {phase.name}
          </span>
          {isCancelled ? (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500">
              Cancelado
            </span>
          ) : (
            <span
              className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${stateBadgeClass(state, isPaused)}`}
            >
              {stateLabel(state, isPaused)}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed line-clamp-2">
          {phase.description}
        </p>
      </div>
    </button>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Main PhaseFlow Component
// ────────────────────────────────────────────────────────────────────────────

export interface PhaseFlowProps {
  phases: Phase[]
  currentPhase: number
  selectedPhaseIdx?: number
  onSelectPhase?: (idx: number) => void
  isPaused?: boolean
  isCancelled?: boolean
  completedPhases?: number
  size?: 'compact' | 'large'
  projectName?: string
}

export function PhaseFlow({
  phases,
  currentPhase,
  selectedPhaseIdx,
  onSelectPhase,
  isPaused = false,
  isCancelled = false,
  completedPhases = 0,
  size = 'large',
  projectName,
}: PhaseFlowProps) {
  const isCompact = size === 'compact'

  if (isCompact) {
    // Compact version for sticky header (just the dots + text)
    return (
      <div className="rounded-xl bg-neutral-50/80 border border-neutral-200/80 overflow-hidden">
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <div className="flex items-center flex-1 min-w-0">
            {phases.map((phase, idx) => {
              const isCurrent = phase.number === currentPhase
              const state = getNodeState(phase.number, currentPhase)

              return (
                <div key={phase.number} className="flex items-center flex-1 last:flex-none">
                  <button
                    onClick={() => onSelectPhase?.(idx)}
                    className="flex-shrink-0 relative group"
                  >
                    <div
                      className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-transform group-hover:scale-110`}
                      style={{
                        ...(isCurrent
                          ? {
                              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`,
                              boxShadow: `0 0 0 2px white, 0 0 0 4px ${GOLD}`,
                            }
                          : state === 'completed'
                            ? { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})` }
                            : { border: '2px dashed #d4d4d4', backgroundColor: '#fafafa' }),
                      }}
                    >
                      {state === 'completed' && <Check className="h-3 w-3 text-white" />}
                      {isCurrent && <phase.icon className="h-3 w-3 text-white" />}
                    </div>
                  </button>

                  {idx < phases.length - 1 && (
                    <div className="flex-1 mx-0.5">
                      <div
                        className="h-[2px] w-full"
                        style={
                          state === 'completed'
                            ? { background: `linear-gradient(90deg, ${GOLD}, ${GOLD_DARK})` }
                            : {
                                backgroundImage: 'repeating-linear-gradient(90deg, #d4d4d4 0px, #d4d4d4 4px, transparent 4px, transparent 8px)',
                              }
                        }
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // Large version for dashboard (full layout with desktop + mobile)
  return (
    <div className="space-y-6">
      {/* Desktop version */}
      <Card className="hidden md:block p-8" style={{ background: 'linear-gradient(135deg, rgb(250, 250, 249) 0%, rgb(255, 255, 255) 40%, rgba(184, 163, 120, 0.06) 100%)' }}>
        <div className="flex items-center gap-2 mb-8">
          <h2 className="text-lg font-semibold text-neutral-800">Fluxo da Obra</h2>
          {projectName && <span className="text-sm text-neutral-400">— {projectName}</span>}
        </div>

        <div className="flex items-start">
          {phases.map((phase, idx) => {
            const state = getNodeState(phase.number, currentPhase)
            return (
              <div key={phase.number} className={`flex items-start ${idx < phases.length - 1 ? 'flex-1' : ''}`}>
                <FlowNode
                  phase={phase}
                  state={state}
                  isSelected={selectedPhaseIdx === idx}
                  onClick={() => onSelectPhase?.(idx)}
                  isPaused={isPaused && phase.number === currentPhase}
                  isCancelled={isCancelled}
                />
                {idx < phases.length - 1 && <FlowConnector filled={!isCancelled && currentPhase > 0 && phase.number < currentPhase} />}
              </div>
            )
          })}
        </div>

        {/* Progress bar */}
        {!isCancelled && (
          <>
            <Separator className="mt-6 mb-4 bg-neutral-100" />
            <div className="flex items-center gap-4">
              <span className="text-xs text-neutral-400 flex-shrink-0">Progresso</span>
              <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${(completedPhases / phases.length) * 100}%`,
                    background: `linear-gradient(90deg, ${GOLD}, ${GOLD_DARK})`,
                  }}
                />
              </div>
              <span className="text-xs font-medium text-neutral-400 flex-shrink-0">
                {completedPhases}/{phases.length}
              </span>
            </div>
          </>
        )}
      </Card>

      {/* Mobile version */}
      <Card className="md:hidden overflow-hidden border-0 shadow-lg">
        <div
          className="p-5"
          style={{
            background: 'linear-gradient(135deg, #fafaf9 0%, #ffffff 40%, rgba(184,163,120,0.06) 100%)',
          }}
        >
          <h2 className="text-lg font-semibold text-neutral-800 mb-5">Fluxo da Obra</h2>
          <div className="flex flex-col">
            {phases.map((phase, idx) => {
              const state = getNodeState(phase.number, currentPhase)
              return (
                <MobileFlowNode
                  key={phase.number}
                  phase={phase}
                  state={state}
                  isSelected={selectedPhaseIdx === idx}
                  onClick={() => onSelectPhase?.(idx)}
                  isPaused={isPaused && phase.number === currentPhase}
                  isCancelled={isCancelled}
                  isLast={idx === phases.length - 1}
                />
              )
            })}
          </div>
        </div>
      </Card>
    </div>
  )
}
